import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Button } from '../components/ui/Button';
import { Label } from '../components/ui/Label';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import type { AuditPage, TenantUser } from '../types/api';

const PAGE_SIZE = 50;

/** Action prefixes the log filter offers (the server matches the start of the action). */
const ACTION_TYPES: { value: string; label: string }[] = [
  { value: '', label: 'All types' },
  { value: 'contact.', label: 'Contacts' },
  { value: 'campaign.', label: 'Campaigns' },
  { value: 'automation.', label: 'Automations' },
  { value: 'user.', label: 'Users' },
  { value: 'auth.', label: 'Sign-in and security' },
];

/** Admin only: who did what. Counts and field names only, never contact data. */
export function AuditLogPage() {
  const { isAdmin } = useAuth();
  const [userId, setUserId] = useState('');
  const [action, setAction] = useState('');

  const users = useQuery({
    queryKey: ['users'],
    queryFn: ({ signal }) => apiFetch<{ users: TenantUser[] }>('/users', { signal }),
    enabled: isAdmin,
  });

  const log = useInfiniteQuery({
    queryKey: ['audit', userId, action],
    initialPageParam: '',
    queryFn: ({ pageParam, signal }) => {
      const q = new URLSearchParams({ limit: String(PAGE_SIZE) });
      if (userId) q.set('userId', userId);
      if (action) q.set('action', action);
      if (pageParam) q.set('before', pageParam);
      return apiFetch<AuditPage>(`/audit?${q.toString()}`, { signal });
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: isAdmin,
  });

  if (!isAdmin) return <Alert>Only administrators can read the audit log.</Alert>;

  const entries = log.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Audit log</h1>
        <MockBadge />
      </header>
      <p className="m-0 text-sm text-muted">
        Every change made by a person in the admin, newest first. Entries show what was changed,
        never the contents of a contact.
      </p>

      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="audit-user">User</Label>
          <select
            id="audit-user"
            className="h-9 rounded-md border border-border bg-card px-2 text-sm"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
          >
            <option value="">All users</option>
            {(users.data?.users ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.email}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="audit-type">Type</Label>
          <select
            id="audit-type"
            className="h-9 rounded-md border border-border bg-card px-2 text-sm"
            value={action}
            onChange={(e) => setAction(e.target.value)}
          >
            {ACTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
      </Card>

      {log.isError ? <Alert>The audit log could not be loaded. Please try again.</Alert> : null}
      {log.isLoading ? <div className="text-muted">Loading…</div> : null}
      {log.data && entries.length === 0 ? (
        <p className="text-muted text-sm m-0">No entries match these filters.</p>
      ) : null}
      {entries.length > 0 ? (
        <div className="border border-border rounded-xl overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-muted">
                <th className="px-3 py-2 font-medium">When</th>
                <th className="px-3 py-2 font-medium">User</th>
                <th className="px-3 py-2 font-medium">Action</th>
                <th className="px-3 py-2 font-medium">Target</th>
                <th className="px-3 py-2 font-medium">Details</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id} className="border-t border-border-soft align-top">
                  <td className="px-3 py-2 whitespace-nowrap">{formatDateTime(e.createdAt)}</td>
                  <td className="px-3 py-2">{e.userEmail ?? 'System'}</td>
                  <td className="px-3 py-2 font-mono text-xs">{e.action}</td>
                  <td className="px-3 py-2">
                    {e.targetType ?? '—'}
                    {e.targetId ? (
                      <span className="block text-xs text-muted">{e.targetId}</span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs break-all">
                    {Object.keys(e.details).length > 0 ? JSON.stringify(e.details) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {log.hasNextPage ? (
        <div>
          <Button
            variant="secondary"
            disabled={log.isFetchingNextPage}
            onClick={() => void log.fetchNextPage()}
          >
            {log.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </Button>
        </div>
      ) : null}
    </>
  );
}
